// Package Imports
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Input,
  Flex,
  Tooltip,
  Checkbox,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { BaseRecord, useNavigation, useTranslate } from '@refinedev/core';
import { useForm, useSimpleList } from '@refinedev/antd';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DropdownInput from 'components/dropdown/dropDownInput';
import apiEndpoint from 'core/common/apiEndPoints';
import resourceName from 'core/common/resourceName';
import replacePipe from 'core/utils/replacePipe';
import DetailFieldsCheckboxGrid from 'pages/reports/payoutReport/DetailFieldsCheckboxGrid';
import constants from 'core/constants/Constants';
import regex from 'core/constants/Regex';
import openNotificationWithIcon from 'core/utils/notification';

type FieldOption = { key: string; value: string; label: string };

const ConfigReport = ({ pageType }) => {
  const t = useTranslate();
  const { list } = useNavigation();
  const { id } = useParams();
  const isShow = pageType === constants.show;

  const { form, formProps, saveButtonProps, onFinish, queryResult } = useForm({
    resource: apiEndpoint.reportConfiguration,
    id,
    action: pageType === constants.create ? 'create' : 'edit',
    dataProviderName: 'reportDataProvider',
    redirect: false,
    queryOptions: {
      enabled: pageType !== constants.create && !!id,
      select: (res: any) => ({
        ...res,
        data: res?.data?.results ?? res?.data,
      }),
    },
    successNotification: false,
    onMutationSuccess: (data: any) => {
      const message =
        data?.data?.message ??
        data?.message ??
        t('notifications.mutationSuccess');
      openNotificationWithIcon(
        constants.success,
        t('notifications.mutationSuccess'),
        message
      );
    },
    errorNotification: (data: any) => ({
      message: 'Error Message',
      description:
        replacePipe(data?.message) || t('notifications.errorMessage'),
      type: 'error',
    }),
  });
  const isLoadingRecord =
    pageType !== constants.create && queryResult?.isLoading;
  const rulePlanId = Form.useWatch('rule_plan_id', { form, preserve: true });
  const detailField = Form.useWatch('detail_fields', { form, preserve: true });
  const { listProps: reportConfigListProps } = useSimpleList({
    resource: `${apiEndpoint.reportConfigList}/?rule_plan_id=${rulePlanId}`,
    dataProviderName: 'reportDataProvider',
    pagination: { mode: 'off' },
    syncWithLocation: false,
    queryOptions: {
      enabled: Boolean(rulePlanId),
    },
  });
  const reportConfigs =
    (reportConfigListProps?.dataSource as BaseRecord)?.results || [];
  const isFlagTakenByAnotherConfig = reportConfigs.some(
    (item: any) =>
      item?.is_used_payment_partiwise_report === true && item?.unique_id !== id
  );
  const { listProps } = useSimpleList({
    resource: `${apiEndpoint.programConfigDetails}`,
    dataProviderName: 'programDataProvider',
    pagination: {
      mode: 'off',
    },
  });
  const frequencyOptionList = (listProps?.dataSource as any)?.results || [];
  const rulePlanList = useMemo(
    () =>
      frequencyOptionList.flatMap((config: any) =>
        (config.rule_plan_unified_list || []).map((item: any) => ({
          key: item.rule_plan_id,
          value: item.rule_plan_id,
          label: item.rule_plan_name,
          workflow_id: config.workflow_id,
          frequency_id: config.frequency_id,
        }))
      ),
    [frequencyOptionList]
  );
  const selectedRulePlan = useMemo(
    () => rulePlanList.find((item) => item.value === rulePlanId),
    [rulePlanList, rulePlanId]
  );
  const workflowId = selectedRulePlan?.workflow_id;
  const frequencyId = selectedRulePlan?.frequency_id;
  const rulePlanName =
    selectedRulePlan?.label ?? queryResult?.data?.data?.rule_plan_name;

  useEffect(() => {
    if (
      pageType === constants.create &&
      rulePlanList.length === 1 &&
      !rulePlanId
    ) {
      form.setFieldValue('rule_plan_id', rulePlanList[0].value);
    }
  }, [rulePlanList, pageType]);
  const handleRulePlanChange = () => {
    form.setFieldValue('detail_fields', undefined);
  };
  const { listProps: dataIndexDrpList } = useSimpleList({
    resource: `${apiEndpoint.getTaskColumns}/?workflow_id=${workflowId}&frequency_id=${frequencyId}&rule_plan_id=${rulePlanId}&task_type=task_details`,
    dataProviderName: 'simulationDataProvider',
    pagination: { mode: 'off' },
    queryOptions: {
      enabled: Boolean(workflowId && frequencyId && rulePlanId),
    },
    errorNotification: (data: BaseRecord) => ({
      message: 'Error Message',
      description:
        replacePipe(data?.message) || t('notifications.errorMessage'),
      type: 'error',
    }),
  });
  const detailFields: FieldOption[] =
    (dataIndexDrpList?.dataSource as any)?.detailFields
      ?.filter(
        (item: string, index: number, arr: string[]) =>
          arr.indexOf(item) === index
      )
      .map((item: string) => ({ key: item, value: item, label: item })) ?? [];

  function goToList() {
    list(resourceName.configReport);
  }
  const pageTitleMap: Record<string, string> = {
    Create: t('pages.configReport.createTitle'),
    Edit: t('pages.configReport.editTitle'),
    Show: t('pages.configReport.showTitle'),
  };
  const pageTitle = pageTitleMap[pageType];
  const onFinishHandler = async (data: any) => {
    await onFinish({
      config_name: data.config_name,
      workflow_id: workflowId,
      frequency_id: frequencyId,
      rule_plan_id: data.rule_plan_id,
      rule_plan_name: rulePlanName,
      detail_fields: data.detail_fields,
      is_used_payment_partiwise_report: data.is_used_payment_partiwise_report,
    });
    goToList();
  };
  if (pageType !== constants.create && isLoadingRecord) return null;

  return (
    <Card>
      <Flex align="center" className="py-xs">
        <Button
          type="text"
          className="mr-sm"
          icon={<ArrowLeftOutlined />}
          onClick={goToList}
        />
        <h4 className="mt-sm">{pageTitle}</h4>
      </Flex>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={onFinishHandler}
        className="mt-md"
        disabled={isShow}
      >
        <Row gutter={[24, 2]}>
          <Col xs={24} md={12} lg={6}>
            <div className="relative custom-dropdown">
              <label className="floating-label">
                {t('pages.configReport.configName')}{' '}
                <span className="a8-error">*</span>
              </label>
              <Form.Item
                name="config_name"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('pages.configReport.configNameRequired'),
                  },
                  {
                    whitespace: true,
                    message: t('pages.configReport.configNameRequired'),
                  },
                  {
                    pattern: regex.ruleNameCheck,
                    message: t('pages.configReport.configNameInvalid'),
                  },
                ]}
              >
                <Input
                  type="text"
                  className="min-w-100 w-100 !h-[45px]"
                  placeholder={t('pages.configReport.placeholder')}
                />
              </Form.Item>
            </div>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <DropdownInput
              label={t('common.rulePlan')}
              className="custom-clear-icon min-w-80 w-80"
              placeholder={t('placeholder.rulePlan')}
              name="rule_plan_id"
              optionList={rulePlanList}
              required={true}
              disabled={isShow || rulePlanList.length === 0}
              onChange={handleRulePlanChange}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <div className="relative custom-dropdown">
              <Flex align="center" className="h-full pt-10 pb-md">
                <Tooltip
                  title={
                    isFlagTakenByAnotherConfig
                      ? t('pages.configReport.checkboxTakenTooltip')
                      : ''
                  }
                >
                  <Form.Item
                    name="is_used_payment_partiwise_report"
                    valuePropName="checked"
                    className="!mb-0"
                  >
                    <Checkbox
                      disabled={
                        isFlagTakenByAnotherConfig ||
                        isShow ||
                        rulePlanId === undefined
                      }
                    >
                      {t('pages.configReport.checkboxLabel')}
                    </Checkbox>
                  </Form.Item>
                </Tooltip>
              </Flex>
            </div>
          </Col>
          <Col span={24}>
            <Form.Item
              name="detail_fields"
              required
              rules={[{ required: true, message: 'Please Select Fields' }]}
              className="mb-0"
            >
              <DetailFieldsCheckboxGrid options={detailFields} />
            </Form.Item>
          </Col>
          {!isShow ? (
            <Col span={24} className="flex justify-end">
              <Form.Item className="mb-0 pt-md">
                <Button
                  {...saveButtonProps}
                  type="primary"
                  size="large"
                  disabled={!rulePlanId || !detailField?.length}
                  className="py-[25px] w-full md:w-fit h-12 "
                >
                  {pageType === constants.edit
                    ? t('buttons.update')
                    : t('buttons.save')}
                </Button>
              </Form.Item>
            </Col>
          ) : null}
        </Row>
      </Form>
    </Card>
  );
};

export default ConfigReport;
